import { gql } from 'apollo-angular';

export const TEST_RUN_PROPERTIES_QUERY = gql`
	query resultsPropertiesForTestRun($uid: ID!) {
		resultsPropertiesForTestRun(testRunUid: $uid) {
			description
			fieldFormat
			identifier
			name
			value
			valueDescription
			variant
			version
			label
		}
	}
`;

export const TEST_RUN_STATISTICS_QUERY = gql`
	query resultsStatisticsForTestRun($uid: ID!) {
		resultsStatisticsForTestRun(uid: $uid) {
			inputStatistics {
				count
				type
			}
		}
	}
`;

export const TEST_RUNS_BY_TEST_CASE_UID_QUERY = gql`
	query resultsTestRunModelsByTestCase($uid: ID!) {
		resultsTestRunModelsByTestCase(testCaseUid: $uid) {
			testSuite {
				testSuiteUid
				itemNo
				version
				name
			}
			testSpecification {
				uid
				version
				name
				itemNo
			}
			testRun {
				testRunUid
				vinNo
				status
				classification
				classificationReason
				environment
				executionTime
				regBy
				regTime
				testResults {
					testResultUid
					result
					resultComment
					testCaseVersion
					testCaseName
					positiveComment
					negativeComment
					troubleReports {
						name
						uri
						testResultUid
						validFrom
						validTo
					}
				}
			}
			testRunType
		}
	}
`;

export const TEST_RUNS_BY_TEST_SUITE_UID_QUERY = gql`
	query resultsTestRunModelsByTestSuite($uid: ID!) {
		resultsTestRunModelsByTestSuite(testSuiteUid: $uid) {
			testRun {
				testRunUid
				environment
				executionTime
				regBy
			}
			testSuite {
				testSuiteUid
				itemNo
				version
				name
				parentTestSuiteUid
				parentVersion
			}
			testSuiteVersion
		}
	}
`;

export const TEST_RUNS_BY_TEST_SPECIFICATION_UID_QUERY = gql`
	query resultsTestRunModelsByTestSpecification($uid: ID!) {
		resultsTestRunModelsByTestSpecification(testSpecificationUid: $uid) {
			testRun {
				testRunUid
				environment
				executionTime
				regBy
			}
			testSpecification {
				uid
				version
				name
				itemNo
			}
		}
	}
`;

export const TEST_RUNS_BY_DATE_QUERY = gql`
	query resultsTestRunModelByDates($startDate: String!, $endDate: String!) {
		resultsTestRunModelByDates(startDate: $startDate, endDate: $endDate) {
			testRun {
				testRunUid
				executionTime
				environment
				vinNo
				classification
				classificationReason
				status
				regBy
				regTime
			}
			testRunType
			testSpecification {
				itemNo
				name
				uid
				version
			}
			testSuite {
				name
				testSuiteUid
				itemNo
				version
			}
			testSuiteVersion
			ecuAndSwIdentifiers {
				name
				swIdentifiers
			}
		}
	}
`;

export const COMPLETE_TEST_RUN_QUERY = gql`
	query resultsCompleteTestRun($uid: ID!) {
		resultsCompleteTestRun(uid: $uid) {
			testRun {
				classification
				classificationReason
				classificationComment
				configuration
				sop
				testPeriod
				environment
				executionTime
				regBy
				regTime
				status
				testRunUid
				vinNo
				testResults {
					failedReason
					testCaseName
					testCaseUid
					testCaseItemNo
					testResultUid
					result
					testSuiteUid
					testSuiteItemNo
					testSuiteName
					testSpecificationUid
					testSpecificationName
					testCaseAlternativeFailedReasons {
						testCaseAlternativeFailedReason
						failedComment
						failedBy
						testCaseAlternativeName
						testCaseAlternativeUid
						testCaseAlternativeVersion
					}
					troubleReports {
						name
						uri
						testResultUid
						validFrom
						validTo
					}
					resultComment
					failedReasonText
					failedBy
					failedReasonComment
					positiveComment
					negativeComment
				}
			}
			testRunType
			testSpecification {
				itemNo
				name
				uid
				version
			}
			testSuite {
				name
				testSuiteUid
				itemNo
				version
			}
			testSuiteVersion
		}
	}
`;

export const ECU_READOUTS_FOR_TEST_RUN_QUERY = gql`
	query resultsEcuReadoutsForTestRun($testRunUid: ID!) {
		resultsEcuReadoutsForTestRun(testRunUid: $testRunUid) {
			ecuDdb
			ecuIdentification
			ecuName
			fileTime
			name
			originTypeUid
			testRunUid
			value
		}
	}
`;

export const ECU_PARAMETERS_FOR_TEST_RUN_QUERY = gql`
	query resultsEcuParametersForTestRun($testRunUid: ID!) {
		resultsEcuParametersForTestRun(testRunUid: $testRunUid) {
			typeUid
			fileUid
			name
			description
			value
			enumValue
			valueType
			ecuName
			ecuFamily
		}
	}
`;

export const REQUIREMENTS_BY_TEST_CASE_QUERY = gql`
	query resultsRequirementsWithTestAnalysisByTestCase($testCaseUid: ID!, $testCaseVersion: Int) {
		resultsRequirementsWithTestAnalysisByTestCase(testCaseUid: $testCaseUid, testCaseVersion: $testCaseVersion) {
			requirement {
				asil
				irmaVersion
				name
				requirementId
				version
			}
			testAnalysisAlternative {
				name
				regBy
				regTime
				uid
				validTo
				version
			}
			testAnalysis {
				name
				regBy
				regTime
				uid
				itemNo
				validTo
				version
			}
		}
	}
`;

export const REQUIREMENTS_QUERY = gql`
	query resultsRequirementsWithTestAnalysis($testResultUid: ID!) {
		resultsRequirementsWithTestAnalysis(testResultUid: $testResultUid) {
			requirement {
				asil
				irmaVersion
				name
				requirementId
				version
			}
			testAnalysisAlternative {
				name
				regBy
				regTime
				uid
				validTo
				version
			}
			testAnalysis {
				name
				regBy
				regTime
				uid
				itemNo
				validTo
				version
			}
		}
	}
`;

export const OFFBOARD_COMPONENTS_QUERY = gql`
	query resultsOffboardComponentsForTestRun($testRunUid: ID!) {
		resultsOffboardComponentsForTestRun(testRunUid: $testRunUid) {
			fileUid
			offboardComponent {
				identifier
				uid
				value
				variant
				version
			}
			regTime
			tpc
			typeUid
		}
	}
`;

export const SOPS_QUERY = gql`
	query resultsSopsForTestRun($testRunUid: ID!) {
		resultsSopsForTestRun(testRunUid: $testRunUid) {
			fpcs {
				familyDescription
				name
				value
				variantDescription
			}
			fileName
			fileTime
			fileUid
			functionParameters {
				family
				name
				parentName
				value
			}
			importTime
			originTypeUid
			regBy
			regTime
			vehicleName
			vinNo
			versionControlVersion
			xpcs {
				name
				value
			}
		}
	}
`;

export const TEST_CASE_STEPS_QUERY = gql`
	query resultsTestCaseSteps($uid: ID!, $version: Int) {
		resultsTestCaseSteps(testCaseUid: $uid, testCaseVersion: $version) {
			expectedResponse
			postcondition
			precondition
			stepNo
			stimuli
		}
	}
`;

export const TEST_CASE_ALTERNATIVES_QUERY = gql`
	query resultsTestCaseAlternatives($uid: ID!, $version: Int) {
		resultsTestCaseAlternatives(testCaseUid: $uid, testCaseVersion: $version) {
			fromDate
			testObjects {
				name
				typeId
				uid
			}
			properties {
				description
				fieldFormat
				identifier
				name
				label
				value
				valueDescription
				variant
				version
			}
			label
			name
			regBy
			regTime
			testGroups
			toDate
			uid
			version
			validFromSop
			validToSop
		}
	}
`;

export const TEST_CASE_DETAILS_QUERY = gql`
	query resultsTestCaseDetails($uid: ID!, $version: Int) {
		resultsTestCaseDetails(testCaseUid: $uid, testCaseVersion: $version) {
			...testCaseProperties
			testCases {
				...testCaseProperties
				testCases {
					...testCaseProperties
					testCases {
						...testCaseProperties
						testCases {
							...testCaseProperties
							testCases {
								...testCaseProperties
								testCases {
									...testCaseProperties
								}
							}
						}
					}
				}
			}
		}
	}
	fragment testCaseProperties on ResultsTestCase {
		tcUid
		itemNo
		description
		version
		owners
		regBy
		regTime
		name
		requirement
		inputTypeId
		script
		resultCount
		linkUid
		parentUid
		operator
		value
		nodeOrder
		restrictedViewAccess
		steps {
			stepNo
			precondition
			stimuli
			expectedResponse
			postcondition
		}
		versions {
			itemUid
			versionNo
			validTo
			regBy
			regTime
		}
		ufs {
			ufNumber
			name
		}
		productProperties {
			uid
			variant
			name
			productCategoryUid
			productCategoryName
		}
		ecus {
			id
			name
			description
			fpcCondition
			familyId
			familyName
		}
		offboardComponents {
			uid
			value
			version
			identifier
			variant
		}
		alternatives {
			uid
			version
			name
			label
			fromDate
			toDate
			validFromSop
			validToSop
			regBy
			regTime
			testGroups
			testObjects {
				uid
				name
				typeId
			}
			properties {
				name
				description
				version
				value
				valueDescription
				fieldFormat
				identifier
				variant
				label
			}
		}
	}
`;

export const TEST_CASE_QUERY = gql`
	query resultsTestCase($uid: ID!, $version: Int) {
		resultsTestCase(testCaseUid: $uid, testCaseVersion: $version) {
			...testCaseProperties
			testCases {
				...testCaseProperties
				testCases {
					...testCaseProperties
					testCases {
						...testCaseProperties
						testCases {
							...testCaseProperties
							testCases {
								...testCaseProperties
								testCases {
									...testCaseProperties
								}
							}
						}
					}
				}
			}
		}
	}
	fragment testCaseProperties on ResultsTestCase {
		tcUid
		itemNo
		description
		version
		owners
		regBy
		regTime
		name
		requirement
		inputTypeId
		script
		resultCount
		linkUid
		parentUid
		operator
		value
		nodeOrder
		restrictedViewAccess
		steps {
			stepNo
			precondition
			stimuli
			expectedResponse
			postcondition
		}
		versions {
			itemUid
			versionNo
			validTo
			regBy
			regTime
		}
		ufs {
			ufNumber
			name
		}
		productProperties {
			uid
			variant
			name
			productCategoryUid
			productCategoryName
		}
		ecus {
			id
			name
			description
			fpcCondition
			familyId
			familyName
		}
		offboardComponents {
			uid
			value
			version
			identifier
			variant
		}
		alternatives {
			uid
			version
			name
			label
			fromDate
			toDate
			validFromSop
			validToSop
			regBy
			regTime
			testGroups
			testObjects {
				uid
				name
				typeId
			}
			properties {
				name
				description
				version
				value
				valueDescription
				fieldFormat
				identifier
				variant
				label
			}
		}
	}
`;

export const TEST_RESULT_QUERY = gql`
	query resultsTestResult($uid: ID!) {
		resultsTestResult(uid: $uid) {
			classification
			classificationComment
			classificationReason
			errorText
			executedBy
			executionTimeTestResult
			executionTimeTestRun
			failedBy
			failedReason
			failedReasonComment
			failedReasonText
			inputType
			mainTestSuiteName
			mainTestSuiteUid
			mainTestSuiteItemNo
			positiveComment
			negativeComment
			mainTestSuiteVersion
			result
			resultText
			resultComment
			testCaseName
			testCaseRestrictedViewAccess
			testCaseUid
			testCaseItemNo
			testCaseVersion
			testResultUid
			testRunType
			testRunUid
			testSpecificationItemNo
			testSpecificationName
			testSpecificationUid
			testSpecificationVersion
			testSuiteName
			testSuiteVersion
			testSuiteUid
			testSuiteItemNo
			vinNo
			linkUid
			vehicleName
			ecus {
				description
				familyId
				familyName
				fpcCondition
				id
				name
			}
			offboardComponents {
				identifier
				uid
				value
				variant
				version
			}
			testCaseAlternativeFailedReasons {
				testCaseAlternativeFailedReason
				failedBy
				failedComment
				testCaseAlternativeName
				testCaseAlternativeUid
				testCaseAlternativeVersion
			}
			troubleReports {
				name
				testResultUid
				uri
				validFrom
				validTo
			}
		}
	}
`;

export const RESULT_PROPERTIES_QUERY = gql`
	query resultsPropertiesForTestResult($testResultUid: ID!) {
		resultsPropertiesForTestResult(testResultUid: $testResultUid) {
			description
			fieldFormat
			identifier
			label
			name
			value
			valueDescription
			variant
			version
		}
	}
`;

export const TEST_SPECIFICATION_QUERY = gql`
	query resultsTestSpecification($uid: ID!, $version: Int) {
		resultsTestSpecification(uid: $uid, version: $version) {
			uid
			itemNo
			name
			version
			owners
			testAnalyses {
				uid
				itemNo
				name
				version
				regBy
				regTime
			}
			regTime
			regBy
			versions {
				itemUid
				versionNo
				validTo
				regBy
				regTime
			}
		}
	}
`;

export const TEST_SUITE_DETAILS_QUERY = gql`
	query resultsTestSuite($uid: ID!, $version: Int) {
		resultsTestSuite(uid: $uid, version: $version) {
			testSuiteUid
			itemNo
			name
			version
			ufs {
				name
				ufNumber
			}
			productProperties {
				name
				productCategoryName
				productCategoryUid
				uid
				variant
			}
			versions {
				itemUid
				regBy
				regTime
				validTo
				versionNo
			}
			offboardComponents {
				identifier
				uid
				value
				version
				variant
			}
			owners
			parentTestSuiteUid
			parentVersion
			description
			regBy
			regTime
			parentUid
			linkUid
			nodeOrder
		}
	}
`;

export const TEST_CASES_FOR_TEST_SUITE_QUERY = gql`
	query resultsTestCasesForTestSuite($uid: ID!, $version: Int) {
		resultsTestCasesForTestSuite(testSuiteUid: $uid, testSuiteVersion: $version) {
			alternatives {
				fromDate
				label
				name
				properties {
					fieldFormat
					description
					identifier
					label
					name
					value
					valueDescription
					variant
					version
				}
				regBy
				regTime
				testGroups
				testObjects {
					name
					typeId
					uid
				}
				toDate
				uid
				validFromSop
				validToSop
				version
			}
			description
			ecus {
				description
				familyId
				familyName
				fpcCondition
				id
				name
			}
			inputTypeId
			linkUid
			name
			nodeOrder
			offboardComponents {
				identifier
				uid
				value
				variant
				version
			}
			operator
			owners
			parentUid
			productProperties {
				name
				productCategoryName
				productCategoryUid
				uid
				variant
			}
			regBy
			regTime
			requirement
			restrictedViewAccess
			resultCount
			script
			steps {
				expectedResponse
				postcondition
				precondition
				stepNo
				stimuli
			}
			tcUid
			itemNo
			testCases {
				alternatives {
					fromDate
					label
					name
					properties {
						description
						fieldFormat
						identifier
						label
						name
						value
						valueDescription
						variant
						version
					}
					regBy
					regTime
					testGroups
					testObjects {
						name
						typeId
						uid
					}
					toDate
					uid
					validFromSop
					validToSop
					version
				}
				description
				ecus {
					description
					familyId
					familyName
					fpcCondition
					id
					name
				}
				inputTypeId
				linkUid
				name
				nodeOrder
				offboardComponents {
					identifier
					uid
					value
					variant
					version
				}
				operator
				owners
				parentUid
				productProperties {
					productCategoryName
					productCategoryUid
					uid
					variant
				}
				regBy
				regTime
				requirement
				restrictedViewAccess
				resultCount
				script
				steps {
					expectedResponse
					postcondition
					precondition
					stepNo
					stimuli
				}
				tcUid
				itemNo
				value
				version
				versions {
					itemUid
					regBy
					regTime
					validTo
					versionNo
				}
				ufs {
					name
					ufNumber
				}
			}
			ufs {
				name
				ufNumber
			}
			value
			versions {
				itemUid
				regBy
				regTime
				validTo
				versionNo
			}
			version
		}
	}
`;

export const ALTERNATIVES_FOR_TEST_SUITE_QUERY = gql`
	query resultsTestSuiteAlternativesForTestSuite($uid: ID!, $version: Int) {
		resultsTestSuiteAlternativesForTestSuite(testSuiteUid: $uid, testSuiteVersion: $version) {
			fromDate
			label
			name
			properties {
				description
				fieldFormat
				identifier
				label
				name
				value
				valueDescription
				variant
				version
			}
			regBy
			regTime
			testGroups
			toDate
			uid
			validFromSop
			validToSop
			version
		}
	}
`;

export const TEST_SUITE_QUERY = gql`
	query resultsTestSuite($uid: ID!, $version: Int) {
		resultsTestSuite(uid: $uid, version: $version) {
			testSuiteUid
			itemNo
			name
			owners
			ufs {
				name
				ufNumber
			}
			version
			productProperties {
				name
				productCategoryName
				productCategoryUid
				uid
				variant
			}
			versions {
				itemUid
				regBy
				regTime
				validTo
				versionNo
			}
			offboardComponents {
				identifier
				uid
				value
				variant
				version
			}
			parentTestSuiteUid
			parentVersion
			childrenTestSuites {
				testRun {
					classification
					classificationComment
					classificationReason
					configuration
					environment
					executionTime
					regBy
					regTime
					sop
					status
					testPeriod
					testRunUid
					vinNo
				}
				testRunType
				testSpecification {
					itemNo
					name
					owners
					regBy
					regTime
					uid
					version
				}
			}
			regBy
			regTime
			parentUid
			nodeOrder
			linkUid
			description
		}
	}
`;

export const TEST_SUITES_FOR_TEST_SUITE_QUERY = gql`
	query resultsTestSuitesForTestSuite($uid: ID!, $version: Int) {
		resultsTestSuitesForTestSuite(uid: $uid, version: $version) {
			name
			nodeOrder
			owners
			regTime
			testSuiteUid
			regBy
			version
			testSuites {
				name
				nodeOrder
				owners
				regBy
				regTime
				testSuiteUid
				version
				testSuites {
					name
					nodeOrder
					regBy
					regTime
					testSuiteUid
					itemNo
					version
				}
			}
		}
	}
`;

export const TROUBLE_REPORTS_FOR_TEST_RESULT_QUERY = gql`
	query resultsTroubleReportsForTestResult($testResultUid: ID!) {
		resultsTroubleReportsForTestResult(testResultUid: $testResultUid) {
			name
			testResultUid
			uri
			validFrom
			validTo
		}
	}
`;

export const ALL_ECUS_QUERY = gql`
	query ecus {
		ecus {
			id
			name
		}
	}
`;

export const ALL_FUNCTIONAL_GROUPS_QUERY = gql`
	query nexusAllFunctionalGroups {
		nexusAllFunctionalGroups {
			description
			name
			uid
		}
	}
`;

export const ALL_GROUPS_QUERY = gql`
	query nexusAllGroups {
		nexusAllGroups {
			description
			name
			regBy
			regTime
			showPublic
			uid
			validTo
		}
	}
`;

export const NEXUS_USER_QUERY = gql`
	query nexusUser($username: String!) {
		nexusUser(username: $username) {
			responseMessages
			returnValue
			success
		}
	}
`;

export const NEXUS_ALL_USERS_QUERY = gql`
	query nexusAllUsers {
		nexusAllUsers {
			functionalGroups {
				functionalGroupName
				functionalGroupUid
				isDefault
			}
			groupUids
			isAdministrator
			isSuperUser
			name
			subjectUid
			username
		}
	}
`;
